import { Card, CardHeader, CardBody, Link } from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionCableContext } from '@/context/ActionCableContext';
import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import { hasAiChatbotEnabled } from '@/patient-app-common/helpers/education_helpers';

export function HomeNav() {
  const { t } = useTranslation();

  const { activeTodos, futureTodos, todoCount } = useAppEvents();

  const { inboxCount, secureMessageThreads } = useContext(ActionCableContext);
  const { data: patient } = useGetPatientQuery();

  const hasAiChatbot = hasAiChatbotEnabled(patient);

  const appointments = useMemo(() => {
    if (!patient) return [];
    return patient.appointments;
  }, [patient]);

  const activeAppointments = appointments.filter((event) =>
    dayjs().isSameOrBefore(event.startAt, 'd')
  );

  const ITEMS = [
    {
      badge: activeAppointments.length,
      description: t('common.link.appointments_hint', {
        count: activeAppointments.length,
      }),
      hidden: !appointments.length,
      icon: <Icon icon="mdi:calendar-alert" className="text-primary" />,
      label: t('common.link.appointments'),
      link: '/appointments',
    },
    {
      badge: todoCount,
      description: t('common.link.todo_hint', { count: todoCount }),
      hidden: !activeTodos.length && !futureTodos.length,
      icon: <Icon icon="mdi:format-list-checks" className="text-primary" />,
      label: t('common.link.todo'),
      link: '/todos',
    },
    {
      description: t('common.link.library_hint'),
      icon: (
        <Icon icon="mdi:book-open-blank-variant" className="text-primary" />
      ),
      label: t('common.link.library'),
      link: '/education',
    },
    {
      description: t('common.link.progress_hint'),
      icon: <Icon icon="mdi:history" className="text-primary" />,
      label: t('common.link.progress'),
      link: '/progress',
    },
    {
      badge: inboxCount,
      description: t('common.link.inbox_hint', { count: inboxCount }),
      count: inboxCount,
      hidden: !secureMessageThreads?.length,
      icon: (
        <Icon icon="mdi:chat-processing-outline" className="text-primary" />
      ),
      label: t('common.link.inbox'),
      link: '/inbox',
    },
    {
      description: t('common.link.answers_hint'),
      icon: (
        <Icon icon="mdi:chat-processing-outline" className="text-primary" />
      ),
      label: t('common.link.answers_short'),
      link: '/education',
      tag: t('common.text.new'),
      hidden: !hasAiChatbot,
    },
  ];

  return (
    <div className="pt-8">
      <h2 className="text-2xl font-bold">{t('common.text.careplan')}</h2>
      <div className="gap-2 grid grid-cols-2 pt-4">
        {ITEMS.filter((item) => !item.hidden).map((item, index) => (
          <Card
            key={index}
            as={Link}
            href={item.link}
            isPressable
            shadow="none"
            className="border m-0.5 border-default-200"
          >
            <CardHeader className="flex flex-row items-center p-4 pb-1">
              <span className="flex-shrink-0">{item.icon}</span>
              <h3 className="font-semibold pl-1">{item.label}</h3>

              {item.badge && !item.tag ? (
                <Icon
                  aria-hidden="true"
                  icon="radix-icons:dot-filled"
                  className="ml-auto mt-1 mr-2 text-danger-500 text-2xl"
                />
              ) : (
                <Icon
                  aria-hidden="true"
                  icon="radix-icons:dot-filled"
                  className="ml-auto mt-1 mr-2 text-2xl text-transparent"
                />
              )}
              {item.tag && (
                <span className="inline-block bg-warning-100 text-warning-700 text-[9px] font-[900] ml-auto px-2 py-0 rounded-[3px] uppercase">
                  {item.tag}
                </span>
              )}
            </CardHeader>
            <CardBody className="p-4 pt-0">
              <p className="text-sm text-gray-500">{item.description}</p>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
